const CASES_CACHE_VERSION_KEY = "cases:cache:version";
const CASES_TABLE_CACHE_PREFIX = "cases:table:";
const CASES_TABLE_DEFAULT_TTL_MS = 60 * 1000;
const CASES_TABLE_MAX_ENTRIES = 8;
const CASES_TABLE_MAX_ENTRY_BYTES = 450 * 1024;

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const hashString = (value) => {
  let hash = 5381;

  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }

  return (hash >>> 0).toString(36);
};

const safeStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const safeStorageSet = (key, value) => {
  localStorage.setItem(key, value);
};

const safeStorageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // no-op
  }
};

const isQuotaExceededError = (error) => {
  if (!error) return false;

  return (
    error.name === "QuotaExceededError" ||
    error.code === 22 ||
    error.code === 1014
  );
};

const getCasesCacheEntries = () => {
  const entries = [];

  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(CASES_TABLE_CACHE_PREFIX)) continue;

      const rawValue = localStorage.getItem(key);
      const parsed = rawValue ? safeParse(rawValue) : null;

      entries.push({
        key,
        rawSize: rawValue ? rawValue.length : 0,
        expiresAt: Number(parsed?.expiresAt || 0),
        storedAt: Number(parsed?.storedAt || 0),
      });
    }
  } catch (error) {
    return [];
  }

  return entries;
};

const removeExpiredCasesCacheEntries = () => {
  const now = Date.now();
  const entries = getCasesCacheEntries();

  entries.forEach((entry) => {
    if (entry.expiresAt > 0 && now > entry.expiresAt) {
      safeStorageRemove(entry.key);
    }
  });
};

const enforceCasesCacheEntryLimit = (maxEntries = CASES_TABLE_MAX_ENTRIES) => {
  const entries = getCasesCacheEntries().sort((a, b) => {
    const aDate = a.storedAt || 0;
    const bDate = b.storedAt || 0;
    return aDate - bDate;
  });

  if (entries.length <= maxEntries) return;

  const itemsToDelete = entries.slice(0, entries.length - maxEntries);
  itemsToDelete.forEach((entry) => safeStorageRemove(entry.key));
};

const tryWriteCasesCache = (cacheKey, serializedValue) => {
  try {
    safeStorageSet(cacheKey, serializedValue);
    return true;
  } catch (error) {
    if (!isQuotaExceededError(error)) {
      return false;
    }

    removeExpiredCasesCacheEntries();
    enforceCasesCacheEntryLimit(CASES_TABLE_MAX_ENTRIES - 1);

    const sortedEntries = getCasesCacheEntries().sort((a, b) => {
      const aDate = a.storedAt || 0;
      const bDate = b.storedAt || 0;
      return aDate - bDate;
    });

    for (let i = 0; i <= sortedEntries.length; i += 1) {
      try {
        safeStorageSet(cacheKey, serializedValue);
        return true;
      } catch (innerError) {
        if (!isQuotaExceededError(innerError)) return false;

        const candidate = sortedEntries[i];
        if (!candidate) return false;
        safeStorageRemove(candidate.key);
      }
    }

    return false;
  }
};

export const getCasesCacheVersion = () => {
  const current = Number(safeStorageGet(CASES_CACHE_VERSION_KEY) || "1");
  return Number.isFinite(current) && current > 0 ? current : 1;
};

export const setCasesCacheVersion = (version) => {
  const next = Number(version);

  if (Number.isFinite(next) && next > 0) {
    try {
      safeStorageSet(CASES_CACHE_VERSION_KEY, String(next));
    } catch (error) {
      // no-op
    }
  }
};

export const buildCasesTableCacheKey = (requestPayload, version) => {
  const raw = JSON.stringify({
    endpoint: "cases/index",
    version,
    payload: requestPayload,
  });

  return `${CASES_TABLE_CACHE_PREFIX}${hashString(raw)}`;
};

export const readCasesTableCache = (cacheKey) => {
  const savedValue = safeStorageGet(cacheKey);
  if (!savedValue) return null;

  const parsed = safeParse(savedValue);
  if (!parsed) {
    safeStorageRemove(cacheKey);
    return null;
  }

  if (Date.now() > Number(parsed?.expiresAt || 0)) {
    safeStorageRemove(cacheKey);
    return null;
  }

  return parsed?.payload || null;
};

export const writeCasesTableCache = (
  cacheKey,
  payload,
  ttlMs = CASES_TABLE_DEFAULT_TTL_MS,
) => {
  const normalizedTtl = Number(ttlMs);
  const expiresAt =
    Date.now() +
    (Number.isFinite(normalizedTtl) && normalizedTtl > 0
      ? normalizedTtl
      : CASES_TABLE_DEFAULT_TTL_MS);

  const envelope = {
    storedAt: Date.now(),
    expiresAt,
    payload,
  };

  const serialized = JSON.stringify(envelope);

  // Evita guardar entradas muy pesadas que saturan localStorage.
  if (serialized.length > CASES_TABLE_MAX_ENTRY_BYTES) {
    return false;
  }

  removeExpiredCasesCacheEntries();
  enforceCasesCacheEntryLimit(CASES_TABLE_MAX_ENTRIES - 1);

  return tryWriteCasesCache(cacheKey, serialized);
};

export const invalidateCasesTableCache = () => {
  const keysToDelete = getCasesCacheEntries().map((entry) => entry.key);

  keysToDelete.forEach((key) => safeStorageRemove(key));
};

export const syncCasesCacheAfterMutation = (backendVersion) => {
  const nextVersion = Number(backendVersion);

  if (Number.isFinite(nextVersion) && nextVersion > 0) {
    setCasesCacheVersion(nextVersion);
  } else {
    setCasesCacheVersion(getCasesCacheVersion() + 1);
  }

  invalidateCasesTableCache();
};
